import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { useEffect, useRef } from 'react';
import { AccessibilityInfo, Platform } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';

interface StatementSheetDotsProps {
  total: number;
  currentIndex: number;
  onSelect: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
  /** What one page is, for the button labels and the position announcement. */
  itemNoun?: string;
  /** The number each page is known by, when it is not its position. */
  itemNumbers?: readonly number[];
}

const DOT_HIT_SLOP = getAccessibleHitSlop(8);

const DOT_BASE_STYLE = {
  width: 8,
  height: 8,
  borderRadius: 999,
};

const ACTIVE_DOT_STYLE = {
  ...DOT_BASE_STYLE,
  width: 18,
};

/**
 * Circular chevron buttons sized for comfortable touch tapping (44pt
 * meets WCAG 2.2 SC 2.5.8 — we render at 48pt to match Material 3 /
 * MUI IconButton sizing). The neutral background-50 fill keeps the
 * buttons present even when disabled, which is preferable to ghost
 * outlines for mobile navigation.
 */
const CHEVRON_BUTTON_SIZE = 48;

/**
 * Carousel-style navigation row. Chevron buttons flank a pagination
 * dot strip — the active dot is filled and elongated, inactive dots
 * are subdued circles. Each dot is independently tappable to jump to
 * that statement; the chevrons step prev / next and announce a
 * navigation analytics event via the `onPrev` / `onNext` handlers
 * passed by the carousel.
 *
 * Returns `null` when `total <= 1` — a single-statement payload has
 * nothing to navigate.
 *
 * Prev is disabled at index 0; next is disabled at the last index.
 * The row exposes the position to assistive tech via a polite live
 * region on web so the active index is announced when the user
 * navigates by swipe, chevron, or dot tap.
 */
export function StatementSheetDots({
  total,
  currentIndex,
  onSelect,
  onPrev,
  onNext,
  itemNoun = 'statement',
  itemNumbers,
}: StatementSheetDotsProps) {
  const { colors } = useColors();

  const isMountedRef = useRef(false);

  const itemLabel = (index: number) =>
    itemNumbers ? `${itemNoun} ${itemNumbers[index]}` : `${itemNoun} ${index + 1} of ${total}`;

  const currentLabel = itemLabel(currentIndex);

  const positionLabel = `${currentLabel.charAt(0).toUpperCase()}${currentLabel.slice(1)}`;

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return;
    }

    if (!isMountedRef.current) {
      isMountedRef.current = true;

      return;
    }

    AccessibilityInfo.announceForAccessibility(positionLabel);
  }, [positionLabel]);

  if (total <= 1) {
    return null;
  }

  const liveRegionProps =
    Platform.OS === 'web'
      ? ({
          'aria-live': 'polite',
          'aria-label': positionLabel,
        } as object)
      : { accessibilityLiveRegion: 'polite' as const };

  const isPrevDisabled = currentIndex <= 0;

  const isNextDisabled = currentIndex >= total - 1;

  return (
    <HStack space="lg" className="items-center justify-center px-4 py-3" {...liveRegionProps}>
      <Pressable
        onPress={onPrev}
        disabled={isPrevDisabled}
        style={{
          width: CHEVRON_BUTTON_SIZE,
          height: CHEVRON_BUTTON_SIZE,
          borderRadius: 999,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.background[50],
        }}
        className={twJoin(
          'web:cursor-pointer web:hover:bg-background-100 web:active:bg-background-200',
          isPrevDisabled && 'opacity-40 web:cursor-not-allowed'
        )}
        {...getAccessibilityProps({
          label: `Previous ${itemNoun}`,
          role: 'button',
        })}>
        <Icon as={ChevronLeft} size="lg" className="text-typography-700" />
      </Pressable>
      <HStack space="xs" className="items-center">
        {Array.from({ length: total }).map((_, index) => {
          const isActive = index === currentIndex;

          return (
            <Pressable
              key={index}
              onPress={() => onSelect(index)}
              hitSlop={DOT_HIT_SLOP}
              style={[
                isActive ? ACTIVE_DOT_STYLE : DOT_BASE_STYLE,
                {
                  backgroundColor: isActive ? colors.primary[500] : colors.outline[200],
                },
              ]}
              {...getAccessibilityProps({
                label: `Go to ${itemLabel(index)}`,
                role: 'button',
              })}
              accessibilityState={{ selected: isActive }}
            />
          );
        })}
      </HStack>
      <Pressable
        onPress={onNext}
        disabled={isNextDisabled}
        style={{
          width: CHEVRON_BUTTON_SIZE,
          height: CHEVRON_BUTTON_SIZE,
          borderRadius: 999,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.background[50],
        }}
        className={twJoin(
          'web:cursor-pointer web:hover:bg-background-100 web:active:bg-background-200',
          isNextDisabled && 'opacity-40 web:cursor-not-allowed'
        )}
        {...getAccessibilityProps({
          label: `Next ${itemNoun}`,
          role: 'button',
        })}>
        <Icon as={ChevronRight} size="lg" className="text-typography-700" />
      </Pressable>
    </HStack>
  );
}
